import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import {
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  useLibraryPartManifestQuery,
  useLibraryPlacementRegistryQuery,
  type ClosingConversationAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import {
  isPartsContent,
  toDraft,
  toPartsContent,
  type DraftPart,
} from "@/lib/assetContent";
import {
  CLOSING_STEPS_ROLE,
  ENGINE_KEYWORD_DONE,
  closingAnchorLanguage,
  starterStepsDocument,
} from "@/lib/closingConversation";
import {
  UNWRITTEN_PROMPT_TEXT,
  joinPartKey,
  manifestFor,
  requiredRoles,
} from "@/lib/namedParts";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { createFieldErrors } from "@/ui/library/NewAssetDialog";
import { LoadingState, UnavailableState } from "@/ui/states";

import {
  NamedReadFailureState,
  type NamedReadFailure,
} from "./NamedReadFailure";

/**
 * Create a closing conversation — one purpose, no free-form fields (#867).
 *
 * ## THE TWO FIELDS THIS DIALOG DOES NOT HAVE
 *
 * There is no "Part key" input and no "Pack placement" box, and both absences
 * are the ticket. The generic `NewAssetDialog` asks for both; for this family
 * the placement is not a coordinate at all — the served registry publishes
 * `placement.kind === "fixed"` with a server-owned pair of path templates, and
 * the database refuses a coordinate on a row of this family outright. A field
 * asking for one would be a control whose every value is refused.
 *
 * So this dialog asks for a NAME, and the two required documents are named by
 * the service's own part manifest rather than by the clinician.
 *
 * ## THE LANGUAGE PREFIX IS THE SERVER'S, AND IT IS A PREREQUISITE
 *
 * This family's part keys are language-scoped, and the pinned runtime opens the
 * conversation language's own documents falling back to ONE anchor. A version
 * whose documents are written at any other key — including a language-NEUTRAL
 * `prompt`/`steps` pair, which is what the generic dialog's manifest roles are —
 * is content the runtime never opens, and the service refuses to adopt it with
 * `placement.content_incomplete`. That refusal arrives at the PROJECT, long
 * after the version became immutable.
 *
 * So the anchor comes from the served placement registry
 * (`runtime.anchorLanguage`) and an unread registry REFUSES the create with a
 * stated reason, rather than guessing a prefix or omitting one. A control whose
 * only outcome is a version no project can pin is the dead control ADR 0001
 * forbids.
 *
 * ## THE STARTER STEP IS NOT A PLACEHOLDER LIKE THE OTHERS
 *
 * `NewMainConcernDialog` seeds a required structured document with `{}`, which
 * is honest for a family whose shape nothing publishes. It cannot work here:
 * #866 published the shape, and `{}` fails it — a steps document must carry at
 * least one step, and at least one instruction must tell the agent to emit the
 * completion token, or the closing turn has no reachable end. A Create seeding
 * `{}` would be a control whose only outcome is a refusal.
 *
 * So the first step is seeded with text that says it is unwritten and carries
 * the token, exactly as `UNWRITTEN_PROMPT_TEXT` does for the prompt half. It
 * reads as unfinished on every surface that shows it, it asserts nothing
 * clinical, and the clinician replaces it in the step editor — which is the
 * post-create step the canonical creation journey describes.
 */

const NAME_MAX_LENGTH = 60;

/**
 * What a refusal computed in the BROWSER says, so nothing is ever swallowed.
 *
 * This dialog builds the whole parts array and hands it to `toPartsContent`
 * before publishing, and that check can refuse for reasons no field on screen
 * owns. A silent `return` there is what made "Create file" a dead control one
 * surface over, so every path out of this submit leaves a sentence behind too.
 */
const REFUSED_CONTENT =
  "This closing conversation's documents could not be prepared, so nothing was created.";

/**
 * What this dialog is waiting on, or why it cannot proceed.
 *
 * `unknown-type` is deliberately not a failure: it is a SUCCESSFUL manifest read
 * that names no `closing_conversation` entry, so the service answered and there
 * is nothing to retry.
 */
type Prerequisite =
  | { state: "pending" }
  | ({ state: "failed" } & NamedReadFailure)
  | { state: "unknown-type" }
  | { state: "ready" };

/** One query's read state as a prerequisite, or `null` when it has succeeded. */
function readState(
  query: {
    isPaused: boolean;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    failureReason: unknown;
  },
  retry: () => void,
): Prerequisite | null {
  // Paused BEFORE pending, in that order: a held retry keeps `status: "pending"`
  // with `isError: false`, so testing pending first is what puts a permanent
  // skeleton over a failure the Editor Service has already answered.
  if (query.isPaused) {
    return {
      state: "failed",
      failure: query.failureReason,
      isPaused: true,
      retry,
    };
  }
  if (query.isError) {
    return { state: "failed", failure: query.error, isPaused: false, retry };
  }
  if (query.isPending) {
    return { state: "pending" };
  }
  return null;
}

export function NewClosingConversationDialog({
  onClose,
  copyFrom,
}: {
  onClose: () => void;
  /** Opens as "create an editable copy of this asset". */
  copyFrom?: ClosingConversationAsset;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  const manifest = useLibraryPartManifestQuery();
  const registry = useLibraryPlacementRegistryQuery();
  const nameErrorId = useId();

  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (copy)` : "");
  const [blocked, setBlocked] = useState<string | null>(null);

  // A copy carries the source's own documents, read at its head.
  const source = useAssetVersionQuery(
    copyFrom?.id ?? "",
    copyFrom ? copyFrom.latestVersion : null,
  );

  // Both recoveries are cancel-then-refetch. A bare `refetch()` is what the
  // paused arm above would otherwise offer, and it fires nothing in exactly the
  // state that offers it.
  const retrySource = useRetryPausedQuery(
    queryKeys.assetVersion(copyFrom?.id ?? "", copyFrom?.latestVersion ?? ""),
  );
  const retryManifest = useRetryPausedQuery(queryKeys.libraryPartManifest);
  const retryRegistry = useRetryPausedQuery(queryKeys.libraryPlacementRegistry);

  const entry = manifestFor(manifest.data, "closing_conversation");
  const required = requiredRoles(entry);
  const anchor = closingAnchorLanguage(registry.data);

  const fieldErrors = create.isError ? createFieldErrors(create.error) : {};
  const generalMessage =
    blocked ??
    (create.isError && Object.keys(fieldErrors).length === 0
      ? create.error instanceof ApiError
        ? create.error.message
        : "The Editor Service could not be reached, so nothing was created."
      : null);

  const busy = create.isPending;
  // A MANIFEST ENTRY THAT NAMES NO REQUIRED DOCUMENT IS THE SAME ANSWER AS NO
  // ENTRY, and it has to be derived here rather than discovered inside `submit`:
  // `requiredRoles` reads the FIRST required family, so an entry whose families
  // are empty yields no parts at all, and a v1 with no parts is refused by
  // `toPartsContent` before any request.
  const prerequisite: Prerequisite = copyFrom
    ? (readState(source, retrySource) ?? { state: "ready" })
    : (readState(manifest, retryManifest) ??
      readState(registry, retryRegistry) ??
      (entry === null || required.length === 0 || anchor === null
        ? { state: "unknown-type" }
        : { state: "ready" }));

  function submit() {
    setBlocked(null);

    let parts: DraftPart[];
    if (copyFrom) {
      const stored = source.isSuccess ? source.data.content : null;
      if (stored === null || !isPartsContent(stored)) {
        setBlocked(
          `The Editor Service returned ${copyFrom.name} in a form this editor does not recognise, so nothing was created.`,
        );
        return;
      }
      parts = toDraft(stored);
    } else {
      if (anchor === null) {
        // Unreachable while Create is gated on `prerequisite`, and stated
        // anyway: a silent return is what makes a dead control.
        setBlocked(
          "The Editor Service did not report which language the runtime reads a closing conversation at, so nothing was created.",
        );
        return;
      }
      parts = required.map((definition, index) => ({
        id: `new-${index}`,
        // THE ANCHOR PREFIX, from the served registry. Without it the pair is
        // language-neutral and the runtime never opens it.
        key: joinPartKey(anchor, definition.role),
        kind: definition.kind as DraftPart["kind"],
        // The service requires both documents at v1, refuses an empty prompt,
        // and refuses a steps document with no completion instruction. Each is
        // therefore created carrying content that states it is unwritten rather
        // than content the clinician was made to invent at creation time.
        text:
          definition.kind === "structured"
            ? definition.role === CLOSING_STEPS_ROLE
              ? starterStepsDocument()
              : "{}"
            : UNWRITTEN_PROMPT_TEXT,
      }));
    }

    const content = toPartsContent(parts);
    if (!content.ok) {
      // NEVER A SILENT RETURN. The clinician pressed Create and is owed an
      // answer, whatever the remaining refusal is.
      setBlocked(content.problems[0]?.message ?? REFUSED_CONTENT);
      return;
    }
    create.mutate(
      {
        assetType: "closing_conversation",
        name: name.trim(),
        content: content.content,
        note: copyFrom
          ? `Copied from "${copyFrom.name}" at ${copyFrom.latestVersion}.`
          : null,
        // NOT SENT AS AN EMPTY STRING: this family has no coordinate placement,
        // and the service refuses one on a row of it.
        placementCoordinate: null,
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(`/library/closing/${created.id}`);
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={copyFrom ? "Create editable copy" : "New closing conversation"}
        description={
          copyFrom
            ? "Creates your own closing conversation starting from this one. The original stays exactly as it is."
            : "The message a session ends on, run after every final track. Its prompt and its first step start unwritten — open the closing conversation afterwards to write them."
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={
                busy || name.trim() === "" || prerequisite.state !== "ready"
              }
              onClick={submit}
            >
              {copyFrom ? "Create copy" : "Create closing conversation"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label="Name"
            description="Short name for this closing conversation. Its prompt and steps are authored afterwards, from its own page."
          >
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  placeholder="e.g. Standard closing, Military intake closing"
                  aria-invalid={fieldErrors.name !== undefined || undefined}
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    fieldErrors.name ? nameErrorId : undefined,
                  )}
                  onChange={(event) => setName(event.target.value)}
                />
                {fieldErrors.name ? (
                  <FieldError id={nameErrorId} message={fieldErrors.name} />
                ) : null}
              </>
            )}
          </Field>

          {copyFrom ? null : (
            <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              The first step is created carrying the runtime&apos;s completion
              token{" "}
              <code dir="ltr" className="font-mono">
                {ENGINE_KEYWORD_DONE}
              </code>
              , because a closing conversation whose steps never instruct it has
              no reachable end and cannot be published. Replace the wording; keep
              a step that names the token.
            </p>
          )}

          {prerequisite.state === "pending" ? (
            <LoadingState
              label={
                copyFrom
                  ? `Loading content from ${copyFrom.name}`
                  : "Loading the documents this type requires"
              }
              rows={2}
            />
          ) : null}

          {prerequisite.state === "failed" ? (
            <NamedReadFailureState
              failure={prerequisite.failure}
              isPaused={prerequisite.isPaused}
              retry={prerequisite.retry}
              subject={copyFrom ? "Asset content" : "Library service documents"}
              title={
                copyFrom
                  ? "This asset's content could not be read"
                  : "The documents this type requires could not be read"
              }
              description={
                copyFrom
                  ? `The Editor Service did not return ${copyFrom.name} at ${copyFrom.latestVersion}, so there is nothing to copy from yet.`
                  : "The Editor Service did not return which documents a closing conversation must carry, and creating one without them would be refused."
              }
              deniedDescription={
                copyFrom
                  ? "You are not authorized to read this asset's content, so it cannot be copied."
                  : "You are not authorized to read which documents a closing conversation must carry."
              }
              offlineDescription={
                copyFrom
                  ? `${copyFrom.name} at ${copyFrom.latestVersion} could not be reached, so there is nothing to copy from yet. It loads on its own when the connection returns.`
                  : "Which documents a closing conversation must carry could not be read, and creating one without them would be refused. It loads on its own when the connection returns."
              }
            />
          ) : null}

          {prerequisite.state === "unknown-type" ? (
            // A SUCCESSFUL manifest read that names no `closing_conversation`
            // entry is not a failure to retry — the service answered, and its
            // answer is that this type has no declared documents.
            <UnavailableState
              title="This type cannot be created here yet"
              description="The Editor Service does not report which documents a closing conversation must carry, or which language the runtime reads them at, so this editor cannot create one that a project could adopt."
            />
          ) : null}

          {generalMessage ? (
            <Notice tone="destructive" role="alert">
              {generalMessage}
            </Notice>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
